import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { Button } from "@carbon/react";
import Form from "../../components/carbon/Form/Form";
import TextField from "../../components/carbon/Form/TextField";
import { changePasswordSchema } from "../../utils";
import { useChangePassword } from "../../hooks/useUsers";
import { useNotification, useUser } from "../../context";
import "./changePasswordForm.scss";

type ChangePasswordFormValues = {
  currentPassword: string;
  password: string;
  confirmPassword: string;
};

/**
 * `POST /auth/change-password` (`AuthController#changePassword`) rejects a wrong
 * `currentPassword` with HTTP 400 and a plain-text body (`ResponseEntity.badRequest().body(
 * "Current password is incorrect")` — not JSON), so `response.data` is the raw string. Matched by
 * substring rather than exact equality so a caller isn't broken by a harmless wording tweak on
 * the backend, and scoped to 400 + this specific phrase so it can't misfire on the (unrelated)
 * "Admin passwords cannot be changed through this endpoint" 400 the same endpoint can return.
 */
function isIncorrectCurrentPasswordError(error: unknown): boolean {
  const response = (
    error as { response?: { status?: number; data?: unknown } } | undefined
  )?.response;
  if (!response || response.status !== 400) return false;
  const data = response.data;
  return typeof data === "string" && data.toLowerCase().includes("current password");
}

export interface ChangePasswordFormProps {
  /** Called after a successful change (normal mode only — reset mode navigates to `/` instead)
   * and from the form's own Cancel button. */
  onCancel: () => void;
  /**
   * `"page"` (default) renders the form as a standalone `.cds-form-surface` panel with its own
   * heading — used in the forced RESET_PASSWORD flow. `"dialog"` drops the panel chrome and the
   * heading (a hosting Carbon modal supplies both) and shifts the field fill to the grey field-02
   * token so inputs still read against the white modal body.
   */
  variant?: "page" | "dialog";
}

/**
 * The change-password form (`POST /auth/change-password`), rebuilt on the Carbon form primitives
 * (Task 5, Profile/Company flow) alongside the Task-2 contract change: the backend now requires a
 * verified `currentPassword` UNLESS the caller is mid RESET_PASSWORD flow (a token-reset caller
 * has no current password to supply — `AuthController#changePassword`'s `resetFlow` branch).
 * `useUser()`'s own `isResetPassword` mirrors that exact branch, so this form both hides the
 * field and stops requiring it (`changePasswordSchema(!isResetPassword)`) in that mode, and omits
 * it from the request body entirely rather than sending it empty.
 *
 * A wrong current password (the one new failure mode this contract change introduces) is
 * surfaced as a field error on `currentPassword`, not a generic toast — see
 * `isIncorrectCurrentPasswordError` above.
 */
const ChangePasswordForm = ({
  onCancel,
  variant = "page",
}: ChangePasswordFormProps) => {
  const isDialog = variant === "dialog";
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isResetPassword, getUser } = useUser();
  const { openNotification } = useNotification();

  const { control, handleSubmit, setError, reset } =
    useForm<ChangePasswordFormValues>({
      resolver: yupResolver(changePasswordSchema(!isResetPassword)),
      defaultValues: { currentPassword: "", password: "", confirmPassword: "" },
    });

  const { mutate: changePassword, isPending } = useChangePassword(
    () => {
      openNotification(t("login.change"));
      getUser();
      reset();
      if (isResetPassword) {
        navigate("/");
      } else {
        onCancel();
      }
    },
    (error) => {
      if (isIncorrectCurrentPasswordError(error)) {
        setError("currentPassword", {
          message: t("changePassword.incorrectCurrent"),
        });
      } else {
        openNotification(t("newMaterialForm.error"));
      }
    }
  );

  const onSubmit = (data: ChangePasswordFormValues) => {
    changePassword(
      isResetPassword
        ? { password: data.password }
        : { password: data.password, currentPassword: data.currentPassword }
    );
  };

  return (
    <div
      className={
        isDialog
          ? "change-password-form change-password-form--dialog"
          : "change-password-form cds-form-surface"
      }
    >
      {!isDialog && (
        <h2 className="change-password-form__title">
          {t("profile.changePassword")}
        </h2>
      )}

      <div className="change-password-form__hints">
        <p>{t("changePassword.title")}</p>
        <ul>
          <li>{t("changePassword.characters")}</li>
          <li>{t("changePassword.uppercase")}</li>
          <li>{t("changePassword.lowercase")}</li>
          <li>{t("changePassword.number")}</li>
          <li>{t("changePassword.special")}</li>
        </ul>
      </div>

      <Form
        handleSubmit={handleSubmit}
        onSubmit={onSubmit}
        noValidate
        className="change-password-form__grid"
      >
        {!isResetPassword && (
          <TextField
            name="currentPassword"
            type="password"
            label={t("changePassword.current")}
            control={control}
            required
          />
        )}
        <TextField
          name="password"
          type="password"
          label={t("changePassword.new")}
          control={control}
          required
        />
        <TextField
          name="confirmPassword"
          type="password"
          label={t("changePassword.confirm")}
          control={control}
          required
        />

        <div className="change-password-form__actions">
          <Button type="button" kind="secondary" onClick={onCancel}>
            {t("cta.cancel")}
          </Button>
          <Button type="submit" disabled={isPending}>
            {t("cta.save")}
          </Button>
        </div>
      </Form>
    </div>
  );
};

export default ChangePasswordForm;
